import React, { useState, useEffect, forwardRef } from "react";
import { useOktaAuth } from "@okta/okta-react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  useTheme,
  ThemeProvider,
  Slide,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  setRef,
  CircularProgress
} from "@mui/material";
import { Area } from "./component/Area";
import { getAreas } from "./utils/ByteConverter";
import { AreaProvider } from "./component/AreaProvider";
import { Add, ArrowDownward, Close, Download, Refresh, UploadFile } from "@mui/icons-material";
import "./App.css"
import { AddStoragePack } from "./component/AddStoragePack";
import { ExcelUploadField } from "./component/ExcelUpload";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const AreaSelector = () => {
  const { authState } = useOktaAuth();
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [addStoragePackVisible, setAddStoragePackVisible] = useState(false);
  const [uploadFormVisible, setUploadFormVisibile] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    if (!authState?.accessToken?.claims) return;

    const areaIds = getAreas(authState.accessToken.claims["quota_management_groups"] || []);
    setAvailableAreas(areaIds);

    if (areaIds.length > 0 && !selectedAreaId) {
      setSelectedAreaId(areaIds[0]);
    }
  }, [authState, selectedAreaId]);

  const downloadFileFromApi = async () => {

    setDownloading(true);

    const params = new URLSearchParams({
      area: selectedAreaId
    });

    const response = await fetch(`${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}area/resources/download?${params.toString()}`, {
      method: "GET",
      headers: {
        "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "Authorization": authState.accessToken.accessToken
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch file:", response.statusText);
      return;
    }

    const disposition = response.headers.get("Content-Disposition");

    console.log(response.headers.get("Content-Dipsposition"));

    let filename = "download.xlsx";

    const blob = await response.blob();

    if (disposition && disposition.includes("filename=")) {
      const matches = disposition.match(/filename="?([^"]+)"?/);
      console.log(matches);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }

    setDownloading(false);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }


  const handleChange = (event) => {
    setSelectedAreaId(event.target.value);
  };

  const refreshInfo = () => {
    setRefreshCounter(prev => prev + 1);
  }

  const renderAddStoragePack = () => {
    return (
      <Dialog
        fullScreen={true}
        open={addStoragePackVisible}
        TransitionComponent={Transition}
      >
        <DialogContent>
          <AppBar sx={{ position: 'relative', backgroundColor: "#A60F2D", color: "white" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setAddStoragePackVisible(!addStoragePackVisible)}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {"Add Storage Pack"}
              </Typography>
            </Toolbar>
          </AppBar>
          <AddStoragePack />
        </DialogContent></Dialog>
    )
  }

  const renderDownloadIndicator = () => {
      return (
          <Dialog
              open={downloading}
              PaperProps={{
                sx: {
                  maxHeight: "none",
                },
              }}
          >
            <DialogContent>
              <AppBar sx={{ position: 'relative', backgroundColor: "#A60F2D", color: "white" }}>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                    {"Downloading Resource Form for Area " + selectedAreaId}
                  </Typography>
              </AppBar>
              <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "40px",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 56,
                    width: "100%",
                    paddingTop: "50px",
                    paddingBottom: "50px"
                  }}
              >
                <CircularProgress
                    size={100}
                    thickness={2}
                    sx={{color: "#A60F2D"}}
                />
              </Box>
            </DialogContent></Dialog>
      )
  }

  const renderUploadForm = () => {
    return (
        <Dialog
            open={uploadFormVisible}
            TransitionComponent={Transition}
            PaperProps={{
              sx: {
                maxHeight: "90vh",
                alignItems: "center",
                maxWidth: "none",
                minWidth: "5vh",
                display: "flex",
                flexDirection: "column",
              },
            }}
        >
          <DialogContent sx={{ overflowY: "auto", flex: 1, padding: "20px" }}>
          <AppBar sx={{ position: 'relative', backgroundColor: "#A60F2D", color: "white" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setUploadFormVisibile(!uploadFormVisible)}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {"Upload Area Resource Documents"}
              </Typography>
            </Toolbar>
          </AppBar>
          <ExcelUploadField />
        </DialogContent></Dialog>
    )
  }

  if (!selectedAreaId) return null;

  return (
    <Box>
      <header className="quotaManagementHeader">
        <h1>WSU Quota Management</h1>
        <Tooltip
          title="Download an export of the area resources."
          placement="bottom"
        >
          <IconButton
            color="inherit"
            onClick={downloadFileFromApi}
            sx={{ position: "absolute", right: 75, top: 16 }}>
            <Download />
          </IconButton></Tooltip>
        <Tooltip
          title="Upload area resources form."
          placement="bottom"
        >
          <IconButton
            color="inherit"
            onClick={() => setUploadFormVisibile(!uploadFormVisible)}
            sx={{ position: "absolute", right: 125, top: 16 }}>
            <UploadFile />
          </IconButton></Tooltip>
        <Tooltip
          title="Refresh the selected organization information."
          placement="bottom"
        >
          <IconButton
            color="inherit"
            onClick={refreshInfo}
            sx={{ position: "absolute", right: 25, top: 16 }}>
            <Refresh />
          </IconButton></Tooltip>
        {authState.accessToken.claims["quota_management_groups"].includes("UISS-QM-Administrator") &&
          <Tooltip
            title="Add a storage pack to an Area"
            placement="bottom"
          >
            <IconButton
              color="inherit"
              onClick={() => setAddStoragePackVisible(!addStoragePackVisible)}
              sx={{ position: "absolute", right: 175, top: 16 }}>
              <Add />
            </IconButton>
          </Tooltip>

        }
      </header>
      <div className="main-container">
        <FormControl fullWidth sx={{
          mb: 2,

          "& .MuiInputLabel-root": {
            color: "black",
          },

          "& .MuiInputLabel-root.Mui-focused": {
            color: "black",
          },

          "& .MuiOutlinedInput-root": {
            color: "black",

            "& fieldset": {
              borderColor: "black",
            },

            "&:hover fieldset": {
              borderColor: "black",
            },

            "&.Mui-focused fieldset": {
              borderColor: "black",
            },
          },
        }}>
          <InputLabel id="area-select-label" >Select Area</InputLabel>
          <Select
            labelId="area-select-label"
            value={selectedAreaId}
            label="Select Area"
            onChange={handleChange}
          >
            {availableAreas.map((areaId) => (
              <MenuItem key={areaId} value={areaId}>
                {areaId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <AreaProvider key={`${selectedAreaId}-${refreshCounter}`} areaId={selectedAreaId} >
          <Area />
        </AreaProvider>
      </div>
      {renderAddStoragePack()}
      {renderUploadForm()}
      {renderDownloadIndicator()}
    </Box>
  );
};
